import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
type Preview = {
  title: string
  sourceImageUrl: string
  input: { artworkId: string; artistId: string }
}
export function SelectArtworkDialog({
  open,
  onClose,
  initialId,
  defaultWeek,
}: {
  open: boolean
  onClose: () => void
  initialId?: string
  defaultWeek: string
}) {
  const navigate = useNavigate()
  const [id, setId] = useState(initialId || '')
  const [selected, setSelected] = useState(initialId || '')
  const [week, setWeek] = useState(defaultWeek)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const intent = useRef<{ key: string; artworkId: string; weekStart: string } | null>(null)
  const preview = useQuery({
    queryKey: ['studio-artwork', selected],
    enabled: open && /^\d{1,10}$/.test(selected),
    retry: false,
    queryFn: async () =>
      (await apiClient.get<Preview>(`/api/v1/films/programmes/selected-artwork/${selected}`)).data,
  })
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v && !busy) onClose()
      }}
    >
      <DialogContent className="max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add an artwork idea</DialogTitle>
          <DialogDescription>
            Choose a published artwork. We’ll refresh Picasso and artist context, then ask you to
            review its original. This does not approve or generate a world.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            setError('')
            setSelected(id.trim())
          }}
          className="flex items-end gap-2"
        >
          <label className="flex-1 text-sm">
            Catalogue artwork ID
            <input
              aria-label="Catalogue artwork ID"
              inputMode="numeric"
              maxLength={10}
              className="mt-1 min-h-11 w-full rounded-lg border px-3"
              value={id}
              onChange={(e) => setId(e.target.value)}
            />
          </label>
          <Button type="submit" variant="outline" disabled={busy || !/^\d{1,10}$/.test(id.trim())}>
            Find artwork
          </Button>
        </form>
        {preview.isFetching && <p role="status">Checking artwork and artist identity…</p>}
        {preview.error && (
          <p role="alert" className="text-sm text-red-700">
            {(preview.error as { response?: { data?: { error?: string } } }).response?.data
              ?.error || 'Artwork unavailable. Check its catalogue ID and published artist profile.'}
          </p>
        )}
        {preview.data && !preview.isFetching && (
          <div className="rounded-xl border p-3">
            <img
              alt={preview.data.title}
              src={preview.data.sourceImageUrl}
              className="mx-auto h-48 max-w-full object-contain"
            />
            <p className="mt-3 font-medium">{preview.data.title}</p>
            <p className="text-xs text-muted-foreground">
              Artwork {preview.data.input.artworkId} · Daily film · music only · reviews required
            </p>
          </div>
        )}
        <label className="text-sm">
          Production week (Monday)
          <input
            aria-label="Selected artwork production week"
            type="date"
            className="mt-1 min-h-11 w-full rounded-lg border px-3"
            value={week}
            onChange={(e) => setWeek(e.target.value)}
            disabled={busy}
          />
        </label>
        {error && (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          Creating an idea does not reserve spend, schedule a post or dispatch generation. Review
          and budget confirmation come next.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={busy || !preview.data || preview.isFetching || selected !== id.trim()}
            onClick={async () => {
              if (!preview.data) return
              setBusy(true)
              setError('')
              const body = { artworkId: preview.data.input.artworkId, weekStart: week }
              if (
                !intent.current ||
                intent.current.artworkId !== body.artworkId ||
                intent.current.weekStart !== week
              )
                intent.current = { ...body, key: crypto.randomUUID() }
              try {
                const { data } = await apiClient.post<{ id: string }>(
                  '/api/v1/films/programmes/selected-artwork',
                  body,
                  { headers: { 'Idempotency-Key': intent.current.key } }
                )
                onClose()
                navigate(`/app/content-studio?view=ideas&programme=${data.id}`)
              } catch (e) {
                setError(
                  (e as { response?: { data?: { error?: string } } }).response?.data?.error ||
                    'Selection outcome unavailable. Retry with the same artwork and week; the request key is retained.'
                )
              } finally {
                setBusy(false)
              }
            }}
          >
            {busy ? 'Preparing idea…' : 'Add idea & review original'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
